"use client";

import { MusicMenu } from "@/components/layout/MusicMenu";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef, type ReactNode } from "react";
import dynamic from "next/dynamic";
import { Menu, X, Search, ShoppingBag, User as UserIcon, ChevronDown } from "lucide-react";
import { PassButton } from "@/components/pass/PassButton";
import { TransitionLink as Link } from "@/components/marketing/PageTransition";
import { E_OUT } from "@/lib/motion";
import { NICHE_COUNT_LIVE, fmt } from "@/lib/catalog-counts";

/* Perf pass: CommandPalette (search input, results list, live-niche fetching)
 * is real weight (~3.1KB + its own logic) that's only ever used if a visitor
 * actually opens search — code-split it so that weight isn't in Nav's own
 * initial bundle. ssr:false is safe: it's a pure client overlay with no SEO
 * content. Only rendered once `searchOpen` is true (see below), so the import()
 * itself doesn't fire until a visitor actually triggers it — NOT on page load. */
const CommandPalette = dynamic(
  () => import("@/components/marketing/CommandPalette").then((m) => m.CommandPalette),
  { ssr: false },
);

type NavLink = { href: string; label: string };

/* Auth state read CLIENT-SIDE (was a server prop from layout.tsx). The layout
 * used to fetch this via cookies(), which forced every page dynamic
 * (cache-control: no-store → no bfcache, no edge caching). The nav now fetches
 * /api/auth/nav-state on mount instead, so the pages themselves stay static/ISR.
 * Trade-off: a signed-in user briefly sees the logged-out CTA before this
 * resolves; logged-out visitors (the vast majority) see the correct state with
 * no flash. */
type AuthState = {
  email: string | null;
  isAdmin: boolean;
};

/* ---------------------------------------------------------------------------
 * APEX-FINAL-SOP / 01-BLOCKING-FIXES §3 — THE NAV OVERFLOWS A LAPTOP.
 *
 * The bar used to render every catalog department as its own top-level link
 * (18 static entries + up to 2 live niche slots ≈ 1,629px), so Social, Fitness
 * and Membership wrapped onto a second row on every laptop under 1,728px.
 *
 * Shape is now FIVE top-level items and ONE primary button:
 *
 *   Shop        mega-menu (departments + browse)
 *   Services    mega-menu
 *   Learn       mega-menu
 *   Pricing     direct link
 *   Membership  direct link, visually distinct
 *   [ Start free → ]
 *
 * The four rules this file is graded against:
 *
 *   1. NEVER two rows. Enforced structurally, not by hope: the <nav> row and
 *      the link <ul> are both flex-nowrap + whitespace-nowrap, so the bar
 *      CANNOT wrap. The desktop row is gated at xl (1280px) — the narrowest
 *      laptop in the SOP's table — and everything below that gets the
 *      hamburger, which is a scroll list and never a second bar row.
 *   2. NOTHING BELOW 19px anywhere in the nav. Every piece of text in this
 *      file is text-[19px] or fontSize: NAV_FONT_PX. The lucide `size=` props
 *      are icon box dimensions, not type. The ⌘K badge and the 13px search
 *      label were the only sub-19px text left; the badge is gone (the shortcut
 *      still works, it moved into the aria-label) and the label is 19px.
 *      This is also why the CTA is built here instead of using <CtaLink>:
 *      CtaLink hard-codes 13px/15px inline and is used site-wide, so raising
 *      it there would repaint every page.
 *   3. Mega-menus open on HOVER on desktop, TAP on touch, and are KEYBOARD
 *      reachable: the trigger is a real <button> (Tab + Enter/Space), Escape
 *      closes and returns focus to it, and focus leaving the group closes it.
 *      Hover is gated behind a (hover: hover) match so a touch device's
 *      synthetic mouseenter can't latch a panel open.
 *   4. Social and Fitness (a live niche slot) leave the top bar — they are
 *      catalog categories, so they sit under Shop → Browse.
 *
 * And the rule that outranks the other four — §3 rule 5, NOTHING MAY REMOVE
 * A URL. Every destination that was in the old bar is still here, one hover
 * and one click away. Mapping, old → new:
 *
 *   /browse-businesses   Shop → Browse → Businesses
 *   /social              Shop → Browse → Social
 *   /templates/<niche>   Shop → Browse → (live niche slots, e.g. Fitness)
 *   /categories          Shop → Departments → Categories
 *   /templates           Shop → Departments → Templates
 *   /notion              Shop → Departments → Notion
 *   /digital-art         Shop → Departments → Wall Art
 *   /stickers            Shop → Departments → Stickers
 *   /3d-models/library   Shop → Departments → 3D Models
 *   /motion              Shop → Departments → Motion
 *   /starter-kits        Shop → Departments → Starter Kits
 *   /bundles             Shop → Departments → Bundles
 *   /ai                  Services → AI Receptionist
 *   /ai-staff            Services → AI Staff
 *   /pulse               Services → Apex Pulse
 *   /websites            Services → Websites
 *   /audit               Services → AI Audit
 *   /tools               Learn → Tools            (was "Free Tools")
 *   /learn               Learn → Learning Center  (was "AI Learning Center")
 *   /membership          top-level, unchanged
 *
 * The panels are ALWAYS in the DOM (hidden with visibility/opacity, not
 * unmounted) for exactly this reason: an unmounted panel would take those 20
 * URLs out of the server-rendered HTML, which is a crawler-visible regression
 * dressed up as a nav fix.
 *
 * ⚠️ ONE THING THIS FILE COULD NOT SATISFY: the SOP asks for Pricing as a
 * direct link, but there is no /pricing route in this repo and
 * digital.apexflowlabs.com/pricing returns 404 (measured 2026-08-19). Rather
 * than ship a nav item that 404s on every page, Pricing points at the tier
 * ladder that actually exists, /membership#tiers. When a real /pricing page
 * lands, this is a one-line change to PRICING_HREF and nothing else.
 * ------------------------------------------------------------------------- */

const NAV_FONT_PX = 19;

type NavColumn = { title: string; items: NavLink[] };
type NavGroup = { label: string; columns: NavColumn[] };
type NavEntry = NavLink | NavGroup;
const isGroup = (e: NavEntry): e is NavGroup => "columns" in e;
const groupItems = (g: NavGroup): NavLink[] => g.columns.flatMap((c) => c.items);

/* Every catalog department. Order is the SOP's order, not alphabetical. */
const SHOP_DEPARTMENTS: NavLink[] = [
  { href: "/categories", label: "Categories" },
  { href: "/templates", label: "Templates" },
  { href: "/notion", label: "Notion" },
  { href: "/digital-art", label: "Wall Art" },
  { href: "/stickers", label: "Stickers" },
  { href: "https://3d.apexflowlabs.com/library", label: "3D Models" },
  { href: "/motion", label: "Motion" },
  { href: "/starter-kits", label: "Starter Kits" },
  { href: "/bundles", label: "Bundles" },
];

/* §3 rule 4 — Social and the live niche slots are catalog categories, so they
 * live under Shop. Businesses joins them: it is a catalog browse surface too,
 * and it was costing a top-level slot the bar does not have. */
const SHOP_BROWSE: NavLink[] = [
  { href: "/social", label: "Social" },
  { href: "/browse-businesses", label: "Businesses" },
];

const SERVICES_ITEMS: NavLink[] = [
  { href: "/ai", label: "AI Receptionist" },
  { href: "/ai-staff", label: "AI Staff" },
  { href: "/pulse", label: "Apex Pulse" },
  { href: "/audit", label: "AI Audit" },
];

const LEARN_ITEMS: NavLink[] = [
  { href: "/answers", label: "Answers" },
  { href: "/tools", label: "Tools" },
  { href: "/tutorials", label: "Tutorials" },
  { href: "/glossary", label: "Glossary" },
  { href: "/compare", label: "Compare" },
  { href: "/blog", label: "Blog" },
  { href: "/learn", label: "Learning Center" },
];

/* /pricing now EXISTS - built 2026-08-19 per AFL-RULINGS-019, Brian:
 * "BUILD IT. A menu item that returns page-not-found is worse than no
 * menu item." It reads every figure from the constants checkout charges
 * from, so it cannot drift from what a customer actually pays. */
const PRICING_HREF = "/pricing";
const START_FREE_HREF = "/auth/sign-up";

const hrefPath = (href: string) => href.split("#")[0];
const isActive = (pathname: string, href: string) => {
  const p = hrefPath(href);
  return pathname === p || pathname.startsWith(p + "/");
};

/* The primary CTA, built locally so it can be 19px (see rule 2 above).
 * Same fill-from-below treatment as CtaLink so the bar still looks like the
 * rest of the site. */
function NavCta({
  href,
  full,
  onClick,
  children,
}: {
  href: string;
  full?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      href={href}
      /* Never prefetch: this is the Start-free → /auth/sign-up button on
       * every page, and prefetching that route pulled its 224 KB
       * auth + Supabase client bundle onto the homepage (100% unused;
       * Lighthouse mobile, 2026-09-06). */
      prefetch={false}
      data-interactive
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`relative inline-flex select-none items-center justify-center overflow-hidden whitespace-nowrap rounded-lg font-medium ${
        full ? "w-full" : ""
      }`}
      style={{
        padding: "10px 18px",
        fontSize: NAV_FONT_PX,
        background: "linear-gradient(135deg,#7a4a22,#c98b4a 24%,#f6dcab 42%,#b87333 62%,#e6b36a)",
        /* Dark text in both states — white-on-accent measured 2.7:1,
         * well under WCAG AA's 4.5:1 floor for normal-weight text
         * (Lighthouse color-contrast, 2026-09-07 fine-tune pass). */
        color: "#0A0A0A",
        transition: `color 280ms ${E_OUT}`,
      }}
    >
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "#f6dcab",
          transform: hov ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "bottom",
          transition: `transform 500ms ${E_OUT}`,
        }}
      />
      <span className="relative">{children}</span>
    </Link>
  );
}

function NavMegaMenu({ group, pathname }: { group: NavGroup; pathname: string }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const items = groupItems(group);
  const anyActive = items.some((i) => isActive(pathname, i.href));
  const panelId = `nav-mega-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  /* Rule 3 — hover is for pointer devices ONLY. On a touch screen the browser
   * fires a synthetic mouseenter on tap and then never fires mouseleave, which
   * would leave the panel stuck open over the page. Touch falls through to the
   * button's onClick. */
  const canHover = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  return (
    <li
      className="relative"
      onMouseEnter={() => {
        if (canHover()) setOpen(true);
      }}
      onMouseLeave={() => {
        if (canHover()) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
          btnRef.current?.focus();
        }
      }}
      onBlur={(e) => {
        /* Close once focus has left the whole group — this is what makes the
         * menu tab-through-able instead of a trap. */
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={btnRef}
        type="button"
        data-interactive
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={panelId}
        className="flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-[16px] font-bold transition-colors hover:text-white"
        style={{ color: anyActive ? "var(--color-accent)" : "rgba(255,255,255,0.86)", borderColor: anyActive ? "#d9a35a" : "rgba(217,163,90,0.35)", background: "rgba(20,16,12,0.55)" }}
      >
        {group.label}
        <ChevronDown
          size={16}
          aria-hidden
          style={{
            opacity: 0.55,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms",
          }}
        />
      </button>

      {/* Always mounted — hidden, not removed, so every URL below stays in the
        * server-rendered HTML for crawlers. `invisible` also pulls the links
        * out of the tab order while the panel is shut. */}
      <div
        id={panelId}
        className={`absolute left-0 top-full z-50 pt-2 transition-opacity duration-150 ${
          open ? "visible opacity-100" : "pointer-events-none invisible opacity-0"
        }`}
      >
        <div
          className="flex gap-6 rounded-xl border p-4"
          style={{
            borderColor: "var(--color-border-default)",
            background: "rgba(16,16,18,0.97)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 24px 64px -24px rgba(0,0,0,0.75)",
          }}
        >
          {group.columns.map((col) => (
            <div key={col.title} className="min-w-[210px]">
              <div
                className="px-3 pb-2 pt-1 font-mono text-[19px] uppercase"
                style={{ color: "rgba(255,255,255,0.38)", letterSpacing: "0.1em" }}
              >
                {col.title}
              </div>
              <ul>
                {col.items.map((i) => (
                  <li key={i.href}>
                    <Link
                      href={i.href}
                      data-interactive
                      onClick={() => setOpen(false)}
                      className="block whitespace-nowrap rounded-lg px-3 py-2 text-[19px] transition-colors hover:bg-white/5"
                      style={{
                        color: isActive(pathname, i.href)
                          ? "var(--color-accent)"
                          : "rgba(255,255,255,0.85)",
                      }}
                    >
                      {i.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </li>
  );
}

/* Fixed top nav. Logo left, five items centre, actions right.
 * Below xl (1280px) it collapses to the hamburger. Translucent dark blur.
 *
 * `featuredNiches` is still fetched in layout.tsx (server) from
 * /api/v1/digital/categories — the prop signature is unchanged so layout.tsx
 * needs no edit — but the slots now land under Shop → Browse instead of
 * eating top-level width. As new niches reach >5 products they auto-appear
 * there, no code change required.
 */
export function Nav({
  featuredNiches = [],
}: {
  featuredNiches?: NavLink[];
}) {
  const NAV_STRUCTURE: NavEntry[] = [
    {
      label: "Shop",
      columns: [
        { title: "Departments", items: SHOP_DEPARTMENTS },
        { title: "Browse", items: [...SHOP_BROWSE, ...featuredNiches] },
      ],
    },
    /* Brian 2026-09-28: "there should be a tab that says websites to choose from". One click to
     * every website template, top level, never buried under Services. */
    { href: "/website-gallery", label: "Websites" },
    { label: "Services", columns: [{ title: "AI & web", items: SERVICES_ITEMS }] },
    { label: "Learn", columns: [{ title: "Resources", items: LEARN_ITEMS }] },
    { href: PRICING_HREF, label: "Pricing" },
  ];

  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const pathname = usePathname();

  /* ⌘K / Ctrl+K opener kept HERE (not inside the dynamically-imported
   * CommandPalette) — Nav is always mounted, so the shortcut works instantly
   * even before the palette's own code has ever been fetched. CommandPalette
   * only mounts (and its chunk only loads) once searchOpen flips true. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Client-side auth — see the AuthState type note. One fetch on mount; the
   * page stays static because the layout no longer reads cookies(). */
  const [authState, setAuthState] = useState<AuthState | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/nav-state", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { signedIn?: boolean; email?: string | null; isAdmin?: boolean } | null) => {
        if (cancelled || !d?.signedIn) return;
        setAuthState({ email: d.email ?? null, isAdmin: Boolean(d.isAdmin) });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /* Memberships were withdrawn from every Apex site (2026-09-25): no Membership link in the nav. */

  return (
    <>
      <header
        className="fixed left-0 right-0 top-0 z-50"
        style={{
          background: "linear-gradient(rgba(11,8,6,0.92), rgba(11,8,6,0.72))",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid var(--color-border-default)",
        }}
      >
        {/* flex-nowrap: rule 1, the bar is structurally incapable of a 2nd row. */}
        <nav className="flex w-full flex-nowrap items-center gap-2 px-4 py-3 sm:gap-4 sm:px-5 md:px-10 lg:px-16">
          {/* Logo */}
          <Link
            href="/"
            data-interactive
            className="flex shrink-0 items-center gap-2.5 text-[15px] font-black uppercase tracking-[0.14em]"
            style={{ color: "var(--color-fg)" }}
            aria-label="Apex Flow Digital, home"
          >
            <span
              aria-hidden
              className="inline-block h-3.5 w-3.5 rotate-45 rounded-[4px]"
              style={{ background: "var(--color-accent)", boxShadow: "0 0 18px var(--color-accent)" }}
            />
            Apex Flow <span style={{ color: "var(--color-accent)" }}>Digital</span>
          </Link>

          {/* Desktop links — ONE row, same row as the logo. */}
          <ul className="hidden shrink-0 flex-nowrap items-center gap-1 whitespace-nowrap xl:flex">
            {NAV_STRUCTURE.map((entry) =>
              isGroup(entry) ? (
                <NavMegaMenu key={entry.label} group={entry} pathname={pathname} />
              ) : (
                <li key={entry.href}>
                  <Link
                    href={entry.href}
                    data-interactive
                    className="whitespace-nowrap rounded-full border px-4 py-2 text-[16px] font-bold transition-colors hover:text-white"
                    style={{
                      color: isActive(pathname, entry.href) ? "var(--color-accent)" : "rgba(255,255,255,0.86)",
                      borderColor: isActive(pathname, entry.href) ? "#d9a35a" : "rgba(217,163,90,0.35)",
                      background: "rgba(20,16,12,0.55)",
                    }}
                  >
                    {entry.label}
                  </Link>
                </li>
              ),
            )}
          </ul>

          {/* Actions — same row, pushed right. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <MusicMenu />
            {/* Niche search. The ⌘K badge and the 13px label are gone: the
              * label is 19px and only shown from 2xl where there is room for
              * it, and the shortcut moved into the aria-label so it is still
              * announced without rendering sub-19px type. */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={`Search ${fmt(NICHE_COUNT_LIVE)} niches, products and pages — shortcut Command K`}
              className="mr-1 hidden items-center gap-2 rounded-full border px-3 py-1.5 text-[19px] text-white transition-all hover:bg-white/10 xl:flex"
              style={{
                borderColor: "rgba(184,115,51,0.32)",
                background: "rgba(184,115,51,0.06)",
              }}
            >
              <Search size={16} aria-hidden style={{ color: "var(--color-accent)" }} />
              <span className="hidden 2xl:inline">Search {fmt(NICHE_COUNT_LIVE)} niches</span>
            </button>

            {/* Cart link — Brian QA: "cart option is still missing".
             * Apex Flow Digital is instant-checkout (every Buy click is a
             * direct Stripe redirect — no traditional cart), so the
             * icon points at /cart, a transparent landing that
             * explains the bundle path via the FBT pattern on every
             * product detail page. */}
            <Link
              href="/cart"
              data-interactive
              aria-label="Cart and bundles"
              className="relative grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10"
            >
              <ShoppingBag size={16} aria-hidden />
            </Link>

            <PassButton className="hidden whitespace-nowrap text-[15px] text-white/85 lg:inline-flex" />

            {authState ? (
              <>
                {authState.isAdmin && (
                  <Link
                    href="/admin" prefetch={false}
                    data-interactive
                    className="hidden rounded-md border px-3 py-1.5 font-mono text-[19px] uppercase xl:inline-block"
                    style={{
                      borderColor: "var(--color-accent)",
                      background: "rgba(217,163,90,0.12)",
                      color: "#fff",
                      letterSpacing: "0.12em",
                    }}
                  >
                    Admin
                  </Link>
                )}
                <Link
                  href="/account/library" prefetch={false}
                  data-interactive
                  aria-label="Account"
                  className="hidden h-9 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-[19px] text-white transition-colors hover:bg-white/10 xl:inline-flex"
                >
                  <UserIcon size={16} aria-hidden />
                  <span className="max-w-[140px] truncate">
                    {authState.email ?? "Account"}
                  </span>
                </Link>
              </>
            ) : (
              <>
                <Link
                  href="/auth/sign-in" prefetch={false}
                  data-interactive
                  className="hidden whitespace-nowrap rounded-md px-2.5 py-2 text-[19px] 2xl:inline-block"
                  style={{ color: "#fff" }}
                >
                  Sign in
                </Link>
                {/* Phones: the bar holds the logo, cart and menu only. Start free lives in the menu, because on a 390px screen the button pushed the menu button off the edge. */}
                <span className="hidden sm:inline-flex">
                  <NavCta href={START_FREE_HREF}>
                    <span className="hidden md:inline">Start free →</span>
                    <span className="md:hidden">Start free</span>
                  </NavCta>
                </span>
              </>
            )}
            <button
              type="button"
              data-interactive
              onClick={() => setOpen((o) => !o)}
              className="grid h-11 w-11 place-items-center xl:hidden"
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X size={22} aria-hidden /> : <Menu size={22} aria-hidden />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            className="max-h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain xl:hidden"
            style={{
              borderTop: "1px solid var(--color-border-default)",
              background: "rgba(10,10,10,0.95)",
            }}
          >
            <ul className="flex flex-col gap-1 px-5 py-4">
              {NAV_STRUCTURE.map((entry) =>
                isGroup(entry) ? (
                  <li key={entry.label} className="mt-1">
                    <div
                      className="px-3 pb-1 pt-2 font-mono text-[19px] uppercase"
                      style={{ color: "rgba(255,255,255,0.42)", letterSpacing: "0.12em" }}
                    >
                      {entry.label}
                    </div>
                    <ul>
                      {groupItems(entry).map((i) => (
                        <li key={i.href}>
                          <Link
                            href={i.href}
                            data-interactive
                            onClick={() => setOpen(false)}
                            className="block rounded-md px-3 py-2.5 text-[19px]"
                            style={{ color: "rgba(255,255,255,0.85)" }}
                          >
                            {i.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={entry.href}>
                    <Link
                      href={entry.href}
                      data-interactive
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-3 py-3 text-[19px]"
                      style={{ color: "#fff" }}
                    >
                      {entry.label}
                    </Link>
                  </li>
                ),
              )}
              <li onClick={() => setOpen(false)}>
                <PassButton className="block w-full py-3 text-left text-[19px] text-white" />
              </li>
              {authState ? (
                <>
                  {authState.isAdmin && (
                    <li className="mt-2">
                      <Link
                        href="/admin" prefetch={false}
                        data-interactive
                        onClick={() => setOpen(false)}
                        className="block rounded-md border px-3 py-3 font-mono text-[19px] uppercase"
                        style={{
                          borderColor: "var(--color-accent)",
                          background: "rgba(217,163,90,0.12)",
                          color: "#fff",
                          letterSpacing: "0.12em",
                        }}
                      >
                        Admin
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link
                      href="/account/library" prefetch={false}
                      data-interactive
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-3 py-3 text-[19px]"
                      style={{ color: "#fff" }}
                    >
                      <UserIcon size={16} aria-hidden className="mr-2 inline" />
                      {authState.email ?? "Account"}
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li className="mt-2">
                    <Link
                      href="/auth/sign-in" prefetch={false}
                      data-interactive
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-3 py-3 text-[19px]"
                      style={{ color: "#fff" }}
                    >
                      Sign in
                    </Link>
                  </li>
                  <li>
                    <NavCta href={START_FREE_HREF} full onClick={() => setOpen(false)}>
                      Start free →
                    </NavCta>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}
      </header>
      {/* Spacer so first section isn't covered by fixed nav. 72px, not the old
        * 64px: 19px type + py-3 + the CTA's 10px padding makes the bar taller
        * than it was, and an under-sized spacer means the fixed bar sits on
        * top of the first heading of every page. */}
      <div className="h-[72px]" aria-hidden />
      {searchOpen && <CommandPalette open={searchOpen} setOpen={setSearchOpen} />}
    </>
  );
}
